/**
 * Orella Health - Specialty Mapping Configuration
 * 
 * Maps patient focus area categories to physician specialties.
 * Used for matching patients with appropriate doctors.
 */

// Patient focus area categories → Doctor specialties
// COMPREHENSIVE MAPPING - covers all focus areas from questionnaires.json
export const CATEGORY_TO_SPECIALTY = {
  // ==================== GENERAL (v213.9 — Sprint 13C-A2) ====================
  // Patients who can't pick a specific body system. AI augments per-category.
  'general': ['General Medicine'],
  'general-complaint': ['General Medicine'],
  // ==================== EAR ====================
  'ear': ['Otolaryngology (ENT)', 'General Medicine'],
  'ear-pain': ['Otolaryngology (ENT)', 'General Medicine'],
  'ear-drainage': ['Otolaryngology (ENT)', 'General Medicine'],
  'muffling-fullness': ['Otolaryngology (ENT)', 'General Medicine'],
  'hearing-loss': ['Otolaryngology (ENT)', 'General Medicine'],
  'ringing-tinnitus': ['Otolaryngology (ENT)', 'General Medicine'],
  'dizziness': ['Otolaryngology (ENT)', 'General Medicine'],
  // ==================== EYE ====================
  'eye': ['Ophthalmology', 'General Medicine'],
  'vision-change': ['Ophthalmology', 'General Medicine'],
  'eye-pain': ['Ophthalmology', 'General Medicine'],
  'eye-redness': ['Ophthalmology', 'General Medicine'],
  'double-vision': ['Ophthalmology', 'General Medicine'],
  'floaters-flashes': ['Ophthalmology', 'General Medicine'],
  'eyelid-problem': ['Ophthalmology', 'General Medicine'],
  'dry-eyes': ['Ophthalmology', 'General Medicine'],
  'itchy-eyes': ['Ophthalmology', 'General Medicine'],
  // ==================== HEAD & NECK ====================
  'head-neck': ['Otolaryngology (ENT)', 'General Medicine'],
  'neck-mass': ['Otolaryngology (ENT)', 'General Medicine'],
  'thyroid': ['Otolaryngology (ENT)', 'General Medicine'],
  'salivary-gland': ['Otolaryngology (ENT)', 'General Medicine'],
  'neck-pain': ['Orthopedics', 'General Medicine'],
  // ==================== HEALTH & WELLNESS ====================
  'health-wellness': ['Allergy & Immunology', 'Endocrinology', 'General Medicine'],
  'fatigue-energy': ['General Medicine'],
  'weight-concerns': ['Endocrinology', 'General Medicine'],
  'allergy': ['Allergy & Immunology', 'General Medicine'],
  'blood-sugar-diabetes': ['Endocrinology', 'General Medicine'],
  'hormone-health': ['General Medicine', 'Endocrinology'],
  
  // ==================== MUSCLE & JOINTS ====================
  'muscle-joints': ['Orthopedics', 'Physical Medicine & Rehab', 'Rheumatology', 'Sports Medicine', 'General Medicine'],
  'joint-pain': ['Rheumatology', 'General Medicine'],
  'back-pain': ['Orthopedics', 'General Medicine'],
  'knee-pain': ['Orthopedics', 'General Medicine'],
  'muscle-pain': ['Rheumatology', 'General Medicine'],
  'joint-swelling': ['Rheumatology', 'General Medicine'],
  'muscle-weakness': ['Orthopedics', 'Rheumatology', 'Pain Management', 'Neurology'],
  
  // ==================== NOSE & SINUS ====================
  'nose': ['Otolaryngology (ENT)', 'General Medicine'],
  'congestion': ['Otolaryngology (ENT)', 'General Medicine'],
  'runny-nose': ['Otolaryngology (ENT)', 'General Medicine'],
  'smell-change': ['Otolaryngology (ENT)', 'General Medicine'],
  'facial-pain-sinus': ['Otolaryngology (ENT)', 'General Medicine'],
  'nasal-pain': ['Otolaryngology (ENT)', 'General Medicine'],
  'nosebleed': ['Otolaryngology (ENT)', 'General Medicine'],
  // ==================== PAIN ====================
  'pain': ['Pain Management', 'Physical Medicine & Rehab', 'General Medicine'],
  'headache': ['Pain Management', 'Neurology'],
  'chest-pain': ['Pain Management', 'Neurology'],
  'pelvic-pain': ['Pain Management', 'Neurology'],
  'chronic-pain': ['Pain Management', 'General Medicine'],
  'personal-injury': ['Pain Management', 'Physical Medicine & Rehab', 'General Medicine'],
  'post-surgical-pain': ['Pain Management', 'General Medicine'],
  // ==================== SKIN ====================
  'skin': ['Dermatology', 'General Medicine'],
  'skin-rash': ['Dermatology', 'General Medicine'],
  'skin-itch': ['Dermatology', 'General Medicine'],
  'growth-mole': ['Dermatology', 'General Medicine'],
  'hives-swelling': ['Dermatology', 'General Medicine'],
  'wound-ulcer': ['Dermatology', 'General Medicine'],
  'discoloration': ['Dermatology', 'General Medicine'],
  'nail-problem': ['Dermatology', 'General Medicine'],
  // ==================== STOMACH & GUT ====================
  'stomach-gut': ['Gastroenterology', 'General Medicine'],
  'abdominal-pain': ['Gastroenterology', 'General Medicine'],
  'nausea-vomiting': ['Gastroenterology', 'General Medicine'],
  'heartburn-reflux': ['Gastroenterology', 'General Medicine'],
  'swallow-difficulty': ['Otolaryngology (ENT)', 'General Medicine'],
  'diarrhea': ['Gastroenterology', 'General Medicine'],
  'constipation': ['Gastroenterology', 'General Medicine'],
  'bloating-gas': ['Gastroenterology', 'General Medicine'],
  'blood-in-stool': ['Gastroenterology', 'General Medicine'],
  'weight-loss': ['Gastroenterology', 'General Medicine'],
  // ==================== THROAT ====================
  'throat': ['Otolaryngology (ENT)', 'General Medicine'],
  'cough': ['Otolaryngology (ENT)', 'General Medicine'],
  'sore-throat': ['Otolaryngology (ENT)', 'General Medicine'],
  'globus-lump': ['Otolaryngology (ENT)', 'General Medicine'],
  'voice-change': ['Otolaryngology (ENT)', 'General Medicine'],
  // ==================== URINARY ====================
  'urinary': ['Urology', 'General Medicine'],
  'pain-with-urination': ['Urology', 'General Medicine'],
  'urinary-frequency': ['Urology', 'General Medicine'],
  'blood-in-urine': ['Urology', 'General Medicine'],
  'difficulty-urinating': ['Urology', 'General Medicine'],
  'urinary-incontinence': ['Urology', 'General Medicine'],
  'flank-pain': ['Urology'],

  // ==================== CANONICAL-SPECIALTY HOME CARDS (v229.0.0) ====================
  // Each new patient card carries one general-concern focus area built on the General
  // template (12 anchors + AI augmentation, primed by the specialty lens in ai_service).
  'heart': ['Cardiology'],
  'heart-general': ['Cardiology'],
  'lungs': ['Pulmonology'],
  'lungs-general': ['Pulmonology'],
  'kidney': ['Nephrology'],
  'kidney-general': ['Nephrology'],
  'blood': ['Hematology'],
  'blood-general': ['Hematology'],
  'womens-health': ['Obstetrics & Gynecology'],
  'womens-health-general': ['Obstetrics & Gynecology'],
  'brain-nerves': ['Neurology'],
  'brain-nerves-general': ['Neurology'],
  'mental-health': ['Psychiatry', 'General Medicine'],
  'mental-health-general': ['Psychiatry', 'General Medicine'],
  'vascular': ['Vascular Surgery'],
  'vascular-general': ['Vascular Surgery'],
  'infection': ['Infectious Disease'],
  'infection-general': ['Infectious Disease'],
  'sleep': ['Sleep Medicine'],
  'sleep-general': ['Sleep Medicine'],

  // v234.6.0 — canonical-specialty home cards (Podiatry + Dental)
  'feet': ['Podiatry'],
  'feet-general': ['Podiatry'],
  'teeth': ['Dental'],
  'teeth-general': ['Dental'],

  // ==================== NEW-AREA FOCUS AREAS (s100, 2026-10-11) ====================
  // 79 tailored focus areas from the 11 new-area question sets. Routing per the approved scope:
  // the area's specialty + Internal Medicine (labelled 'General Medicine' in the directory);
  // Snoring/Apnea and CPAP also route to ENT. Generated by scripts/new_areas/build_new_areas_catalog.py.
  'heart-chest-pain': ['Cardiology', 'General Medicine'],
  'heart-palpitations': ['Cardiology', 'General Medicine'],
  'heart-dyspnea': ['Cardiology', 'General Medicine'],
  'heart-syncope': ['Cardiology', 'General Medicine'],
  'heart-edema': ['Cardiology', 'General Medicine'],
  'heart-hypertension': ['Cardiology', 'General Medicine'],
  'heart-prevention': ['Cardiology', 'General Medicine'],
  'heart-abnormal-test': ['Cardiology', 'General Medicine'],
  'heart-preop': ['Cardiology', 'General Medicine'],
  'lungs-dyspnea': ['Pulmonology', 'General Medicine'],
  'lungs-wheezing': ['Pulmonology', 'General Medicine'],
  'lungs-cough': ['Pulmonology', 'General Medicine'],
  'lungs-hemoptysis': ['Pulmonology', 'General Medicine'],
  'lungs-abnormal-scan': ['Pulmonology', 'General Medicine'],
  'lungs-copd-smoking': ['Pulmonology', 'General Medicine'],
  'lungs-infections': ['Pulmonology', 'General Medicine'],
  'lungs-preop': ['Pulmonology', 'General Medicine'],
  'brain-headache': ['Neurology', 'General Medicine'],
  'brain-numbness': ['Neurology', 'General Medicine'],
  'brain-weakness': ['Neurology', 'General Medicine'],
  'brain-memory': ['Neurology', 'General Medicine'],
  'brain-movement': ['Neurology', 'General Medicine'],
  'brain-seizure': ['Neurology', 'General Medicine'],
  'brain-dizziness': ['Neurology', 'General Medicine'],
  'womens-periods': ['Obstetrics & Gynecology', 'General Medicine'],
  'womens-pelvic-pain': ['Obstetrics & Gynecology', 'General Medicine'],
  'womens-menopause': ['Obstetrics & Gynecology', 'General Medicine'],
  'womens-discharge': ['Obstetrics & Gynecology', 'General Medicine'],
  'womens-breast': ['Obstetrics & Gynecology', 'General Medicine'],
  'womens-contraception': ['Obstetrics & Gynecology', 'General Medicine'],
  'womens-preconception': ['Obstetrics & Gynecology', 'General Medicine'],
  'womens-fertility': ['Obstetrics & Gynecology', 'General Medicine'],
  'womens-pelvic-floor': ['Obstetrics & Gynecology', 'General Medicine'],
  'kidney-labs': ['Nephrology', 'General Medicine'],
  'kidney-stones': ['Nephrology', 'General Medicine'],
  'kidney-urine-findings': ['Nephrology', 'General Medicine'],
  'kidney-electrolytes': ['Nephrology', 'General Medicine'],
  'kidney-imaging': ['Nephrology', 'General Medicine'],
  'blood-anemia': ['Hematology', 'General Medicine'],
  'blood-bleeding': ['Hematology', 'General Medicine'],
  'blood-clots': ['Hematology', 'General Medicine'],
  'blood-lymph-nodes': ['Hematology', 'General Medicine'],
  'blood-abnormal-test': ['Hematology', 'General Medicine'],
  'veins-varicose': ['Vascular Surgery', 'General Medicine'],
  'veins-claudication': ['Vascular Surgery', 'General Medicine'],
  'veins-raynaud': ['Vascular Surgery', 'General Medicine'],
  'veins-wound': ['Vascular Surgery', 'General Medicine'],
  'veins-post-clot': ['Vascular Surgery', 'General Medicine'],
  'veins-artery-finding': ['Vascular Surgery', 'General Medicine'],
  'infection-fever': ['Infectious Disease', 'General Medicine'],
  'infection-recurrent': ['Infectious Disease', 'General Medicine'],
  'infection-travel': ['Infectious Disease', 'General Medicine'],
  'infection-exposure': ['Infectious Disease', 'General Medicine'],
  'infection-wound': ['Infectious Disease', 'General Medicine'],
  'infection-prep': ['Infectious Disease', 'General Medicine'],
  'infection-vaccines': ['Infectious Disease', 'General Medicine'],
  'sleep-apnea': ['Sleep Medicine', 'General Medicine', 'Otolaryngology (ENT)'],
  'sleep-cpap': ['Sleep Medicine', 'General Medicine', 'Otolaryngology (ENT)'],
  'sleep-insomnia': ['Sleep Medicine', 'General Medicine'],
  'sleep-sleepiness': ['Sleep Medicine', 'General Medicine'],
  'sleep-rls': ['Sleep Medicine', 'General Medicine'],
  'sleep-parasomnia': ['Sleep Medicine', 'General Medicine'],
  'sleep-circadian': ['Sleep Medicine', 'General Medicine'],
  'feet-heel': ['Podiatry', 'General Medicine'],
  'feet-toe': ['Podiatry', 'General Medicine'],
  'feet-ankle': ['Podiatry', 'General Medicine'],
  'feet-arch': ['Podiatry', 'General Medicine'],
  'feet-numb': ['Podiatry', 'General Medicine'],
  'feet-nail-skin': ['Podiatry', 'General Medicine'],
  'feet-swelling': ['Podiatry', 'General Medicine'],
  'teeth-toothache': ['Dental', 'General Medicine'],
  'teeth-gums': ['Dental', 'General Medicine'],
  'teeth-jaw': ['Dental', 'General Medicine'],
  'teeth-broken': ['Dental', 'General Medicine'],
  'teeth-mouth-sore': ['Dental', 'General Medicine'],
  'teeth-dry-mouth': ['Dental', 'General Medicine'],
  'teeth-bad-breath': ['Dental', 'General Medicine'],
  'teeth-wisdom': ['Dental', 'General Medicine'],
  'teeth-grinding': ['Dental', 'General Medicine'],
  // s122 (2026-10-28): Mental Health focus areas — Psychiatry + Internal Medicine (Dr. Rami's ruling).
  'mh-depression': ['Psychiatry', 'General Medicine'],
  'mh-anxiety': ['Psychiatry', 'General Medicine'],
  'mh-bipolar': ['Psychiatry', 'General Medicine'],
  'mh-trauma': ['Psychiatry', 'General Medicine'],
  'mh-adhd': ['Psychiatry', 'General Medicine'],
  'mh-substance': ['Psychiatry', 'General Medicine'],
  'mh-psychosis': ['Psychiatry', 'General Medicine'],
  'mh-eating': ['Psychiatry', 'General Medicine'],
  'mh-meds': ['Psychiatry', 'General Medicine'],

  // Referral-only specialties (no home card; questionnaire exists so referred patients can intake)
  'plastic-surgery': ['Plastic Surgery'],
  'plastic-surgery-general': ['Plastic Surgery'],
  'anesthesiology': ['Anesthesiology'],
  'anesthesiology-general': ['Anesthesiology'],

  // Nested focus areas under existing cards (v229.0.0)
  'hearing-concern': ['Audiology', 'Otolaryngology (ENT)', 'General Medicine'],
  'sports-injury': ['Sports Medicine', 'General Medicine'],
  'rehab-mobility': ['Physical Medicine & Rehab', 'General Medicine'],

  // ==================== ORIGINAL AREAS REBUILT (catalog 3.7, 2026-10-12) ====================
  // Dr. Rami 2026-10-11: every concern routes to its specialty + Internal Medicine ('General Medicine').
  // Generated from scripts/original_areas/out/routing.json (build_catalog_37.py).
  'mens-testicle': ['Urology', 'General Medicine'],
  'mens-erection': ['Urology', 'General Medicine'],
  'mens-penis': ['Urology', 'General Medicine'],
  'mens-psa': ['Urology', 'General Medicine'],
  'mens-pelvic-pain': ['Urology', 'General Medicine'],
  'mens-low-t': ['Urology', 'General Medicine'],
  'urinary-general': ['Urology', 'General Medicine'],
  'womens-urination-pain': ['Obstetrics & Gynecology', 'General Medicine'],
  'womens-urinary-frequency': ['Obstetrics & Gynecology', 'General Medicine'],
  'eye-injury': ['Ophthalmology', 'General Medicine'],
  'eye-general': ['Ophthalmology', 'General Medicine'],
  'yellow-skin': ['Gastroenterology', 'General Medicine'],
  'rectal-problem': ['Gastroenterology', 'General Medicine'],
  'belly-bulge': ['Gastroenterology', 'General Medicine'],
  'stomach-general': ['Gastroenterology', 'General Medicine'],
  'noisy-breathing': ['Otolaryngology (ENT)', 'General Medicine'],
  'tonsil-stones': ['Otolaryngology (ENT)', 'General Medicine'],
  'throat-general': ['Otolaryngology (ENT)', 'General Medicine'],
  'neck-general': ['Otolaryngology (ENT)', 'General Medicine'],
  'itchy-ear': ['Otolaryngology (ENT)', 'General Medicine'],
  'ear-general': ['Otolaryngology (ENT)', 'General Medicine'],
  'nose-general': ['Otolaryngology (ENT)', 'General Medicine'],
  'acne-rosacea': ['Dermatology', 'General Medicine'],
  'hair-loss': ['Dermatology', 'General Medicine'],
  'boil': ['Dermatology', 'General Medicine'],
  'heavy-sweating': ['Dermatology', 'General Medicine'],
  'skin-general': ['Dermatology', 'General Medicine'],
  'shoulder-pain': ['Orthopedics', 'General Medicine'],
  'hip-pain': ['Orthopedics', 'General Medicine'],
  'hand-wrist': ['Orthopedics', 'General Medicine'],
  'elbow-pain': ['Orthopedics', 'General Medicine'],
  'bone-health': ['Orthopedics', 'General Medicine'],
  'muscle-joints-general': ['Orthopedics', 'General Medicine'],
  'nerve-pain': ['Pain Management', 'General Medicine'],
  'widespread-pain': ['Pain Management', 'General Medicine'],
  'pain-general': ['Pain Management', 'General Medicine'],
  'thyroid-function': ['Endocrinology', 'General Medicine'],
  'checkup': ['General Medicine'],
  'health-wellness-general': ['General Medicine'],
}


// All available doctor specialties (for signup, settings, and admin — single source of truth)
export const DOCTOR_SPECIALTIES = [
  'Allergy & Immunology',
  'Anesthesiology',
  'Audiology',
  'Cardiology',
  'Dental',
  'Dermatology',
  'Endocrinology',
  'Gastroenterology',
  'General Medicine',
  'Hematology',
  'Infectious Disease',
  'Nephrology',
  'Neurology',
  'Obstetrics & Gynecology',
  'Oncology',
  'Ophthalmology',
  'Orthopedics',
  'Otolaryngology (ENT)',
  'Pain Management',
  'Physical Medicine & Rehab',
  'Plastic Surgery',
  'Podiatry',
  'Psychiatry',
  'Pulmonology',
  'Rheumatology',
  'Sleep Medicine',
  'Sports Medicine',
  'Urology',
  'Vascular Surgery',
]

// Specialty display info (icons and colors)
// v221.1.9: All specialty chips collapsed to a single canonical Ice Blue + Navy
// treatment per the canonical color audit. The icon emoji carries per-specialty
// recall; the chip chrome stays uniform across the brand surface. This file is
// byte-identical across MD panel, patient app, and admin panel (project rule).
const SPECIALTY_CHIP_COLOR = 'bg-[#E9F8FF] text-[#15354F]'

export const SPECIALTY_INFO = {
  'Allergy & Immunology': { icon: '🤧', color: SPECIALTY_CHIP_COLOR },
  'Anesthesiology': { icon: '💤', color: SPECIALTY_CHIP_COLOR },
  'Audiology': { icon: '🔊', color: SPECIALTY_CHIP_COLOR },
  'Cardiology': { icon: '❤️', color: SPECIALTY_CHIP_COLOR },
  'Dental': { icon: '🦷', color: SPECIALTY_CHIP_COLOR },
  'Dermatology': { icon: '🧴', color: SPECIALTY_CHIP_COLOR },
  'Emergency Medicine': { icon: '🚑', color: SPECIALTY_CHIP_COLOR },
  'Endocrinology': { icon: '🦋', color: SPECIALTY_CHIP_COLOR },
  'General Medicine': { icon: '🩺', color: SPECIALTY_CHIP_COLOR },
  'Gastroenterology': { icon: '🏥', color: SPECIALTY_CHIP_COLOR },
  'Geriatrics': { icon: '🧓', color: SPECIALTY_CHIP_COLOR },
  'Hematology': { icon: '🩸', color: SPECIALTY_CHIP_COLOR },
  'Infectious Disease': { icon: '🦠', color: SPECIALTY_CHIP_COLOR },
  'Nephrology': { icon: '🫘', color: SPECIALTY_CHIP_COLOR },
  'Neurology': { icon: '🧠', color: SPECIALTY_CHIP_COLOR },
  'Obstetrics & Gynecology': { icon: '🤰', color: SPECIALTY_CHIP_COLOR },
  'Oncology': { icon: '🎗️', color: SPECIALTY_CHIP_COLOR },
  'Ophthalmology': { icon: '👁️', color: SPECIALTY_CHIP_COLOR },
  'Orthopedics': { icon: '🦴', color: SPECIALTY_CHIP_COLOR },
  'Otolaryngology (ENT)': { icon: '👂', color: SPECIALTY_CHIP_COLOR },
  'Pain Management': { icon: '💉', color: SPECIALTY_CHIP_COLOR },
  'Pediatrics': { icon: '👶', color: SPECIALTY_CHIP_COLOR },
  'Physical Medicine & Rehab': { icon: '🏋️', color: SPECIALTY_CHIP_COLOR },
  'Plastic Surgery': { icon: '✨', color: SPECIALTY_CHIP_COLOR },
  'Podiatry': { icon: '🦶', color: SPECIALTY_CHIP_COLOR },
  'Psychiatry': { icon: '🧩', color: SPECIALTY_CHIP_COLOR },
  'Pulmonology': { icon: '🫁', color: SPECIALTY_CHIP_COLOR },
  'Rheumatology': { icon: '🤲', color: SPECIALTY_CHIP_COLOR },
  'Sleep Medicine': { icon: '😴', color: SPECIALTY_CHIP_COLOR },
  'Sports Medicine': { icon: '⚽', color: SPECIALTY_CHIP_COLOR },
  'Surgery (General)': { icon: '🔪', color: SPECIALTY_CHIP_COLOR },
  'Urology': { icon: '💧', color: SPECIALTY_CHIP_COLOR },
  'Vascular Surgery': { icon: '🫀', color: SPECIALTY_CHIP_COLOR },
  'Other': { icon: '➕', color: SPECIALTY_CHIP_COLOR },
}

// Helper: Get matching specialties for a patient's case category
export function getMatchingSpecialties(categoryId) {
  const normalizedId = categoryId?.toLowerCase().replace(/\s+/g, '-')
  return CATEGORY_TO_SPECIALTY[normalizedId] || ['General Medicine']
}

// Helper: Check if a doctor's specialty matches a case category
export function doctorMatchesCategory(doctorSpecialty, categoryId) {
  const matchingSpecialties = getMatchingSpecialties(categoryId)
  return matchingSpecialties.includes(doctorSpecialty) || doctorSpecialty === 'General Medicine'
}

export default {
  CATEGORY_TO_SPECIALTY,
  DOCTOR_SPECIALTIES,
  SPECIALTY_INFO,
  getMatchingSpecialties,
  doctorMatchesCategory,
}
